import {hpcAttrs} from '@github-ui/hpc'
import {Fragment, useEffect, useRef, useState} from 'react'
import type {ReactNode} from 'react'
import type {Repository} from '@github-ui/current-repository'
import {useRoutePayload} from '@github-ui/react-core/use-route-payload'
import type {HomeRoutePayload, Ruleset} from '@github-ui/repos-rules-types/rules-types'
import {Blankslate} from '../components/Blankslate'
import {BorderBox} from '../components/BorderBox'
import {BranchSelector} from '../components/BranchSelector'
import {Subhead} from '../components/Subhead'
import {RulesetList} from '../components/RulesetList'
import {FlashUpsellBanner, UpsellBanner} from '../components/UpsellBanner'
import {useRelativeNavigation} from '../hooks/use-relative-navigation'
import {NewRulesetButton} from '../components/NewRulesetButton'
import {capitalize} from '../helpers/string'
import {sortSourceTypes} from '../helpers/utils'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {useIsStafftools} from '../hooks/use-is-stafftools'
import {DismissibleFlashOrToast, type FlashAlert} from '@github-ui/dismissible-flash'
import {useRuleStrings} from '../hooks/use-rule-strings'
import {useUpsellInfoContext} from '../contexts/UpsellInfoContext'
import {RuleConfigList} from '@github-ui/rule-config-list'
/* eslint-disable-next-line primer-react/no-deprecated-flash -- Existing Flash usage should be migrated to Banner. */
import {Flash, Link, Banner} from '@primer/react'
import {useClickAnalytics} from '@github-ui/use-analytics'
import {useFeatureFlag} from '@github-ui/react-core/use-feature-flag'
import {ImportBranchProtectionDialog} from '@github-ui/import-branch-protection-dialog'
import {branchProtectionsPath} from '@github-ui/paths'

import styles from './OverviewPage.module.css'
import {useRuleFeatures} from '../hooks/use-rule-features'
import {RedundantProtectedBranchesBanner} from '../components/RedundantProtectedBranchesBanner'

// Matches the canonical `Learn more: <url>` tail on the flash message so the URL
// can be rendered as an anchor while the rest of the message passes through the backtick tokenizer.
const TRAILING_LEARN_MORE_REGEX = /^([\s\S]*?)\s+Learn more:\s+(\S+)\s*$/
const BACKTICK_REGEX = /`([^`]+)`/g

function renderFlashMessage(message: string): ReactNode {
  const trailingMatch = message.match(TRAILING_LEARN_MORE_REGEX)
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const messageBody = trailingMatch ? trailingMatch[1]! : message
  const learnMoreUrl = trailingMatch?.[2]

  const messageParts = messageBody.split(BACKTICK_REGEX).map((part, index) =>
    // The split result is positionally stable: even indexes are plain text,
    // odd indexes are the backtick-wrapped tokens. Index is a valid key here.
    // eslint-disable-next-line @eslint-react/no-array-index-key
    index % 2 === 1 ? <code key={index}>{part}</code> : part,
  )

  if (!learnMoreUrl) {
    return <>{messageParts}</>
  }

  return (
    <>
      {messageParts}{' '}
      <Link
        href={learnMoreUrl}
        className="Link--inTextBlock"
        aria-label="Learn more about pull_request_target restrictions"
      >
        Learn more
      </Link>
    </>
  )
}

export function OverviewPage() {
  const {
    source,
    sourceType,
    rulesets,
    rulesetConversionInfo,
    matchingRulesets,
    editableRulesets,
    branch,
    branchListCacheKey,
    readOnly,
    paths,
    flashMessages = [],
  } = useRoutePayload<HomeRoutePayload>()
  const {navigate, resolvePath} = useRelativeNavigation()
  const upsellInfo = useUpsellInfoContext()
  const [flashAlert, setFlashAlert] = useState<FlashAlert>({message: '', variant: 'default'})
  const flashRef = useRef<HTMLDivElement | null>(null)
  const headingRef = useRef<HTMLHeadingElement | null>(null)
  const isStafftools = useIsStafftools()
  const isRuleConfigListEnabled = isFeatureEnabled('rule_config_list') || isStafftools
  // Track the dismissed source so a dismissal doesn't leak across soft navigation (same component
  // instance is reused); a different source.id makes the banner reappear.
  const [dismissedSourceId, setDismissedSourceId] = useState<number | null>(null)
  const conversionBannerDismissed = dismissedSourceId === source.id
  /* eslint-disable-next-line @github-ui/github-monorepo/no-use-feature-flags --
    TODO: Migrate to isFeatureEnabled from @github-ui/feature-flags. See
    https://thehub.github.com/epd/engineering/dev-practicals/frontend/react/data-router/migration/app-payload-deprecation/ */
  const convertToRulesetUIBanner = useFeatureFlag('convert_to_ruleset_ui_banner')

  const {heading, alphaOrBeta, headingText, rulesetOrPolicy, rulesetsOrPolicies} = useRuleStrings()
  const {supportedTargets} = useRuleFeatures()
  const supportsGitRefTargets = supportedTargets.includes('branch') || supportedTargets.includes('tag')

  useEffect(() => {
    flashRef.current?.focus()
  }, [flashAlert, flashRef])

  const rulesetsBySource = rulesets.reduce<Record<string, Ruleset[]>>((map, item) => {
    // if a branch/ref is specified for a supported git-ref target, filter based on whether each ruleset is applied
    if (supportsGitRefTargets && branch && !matchingRulesets.includes(item.id)) {
      return map
    }

    const key = item.source.type
    if (!map[key]) {
      map[key] = []
    }

    map[key].push(item)
    return map
  }, {})

  const inheritedSourceTypes = Object.keys(rulesetsBySource).sort(sortSourceTypes)

  const hasFilteredRulesets = rulesets.length > 0 && inheritedSourceTypes.length === 0

  const {sendClickAnalyticsEvent} = useClickAnalytics()

  const analytics = {
    convert: {
      category: 'CONVERT_TO_RULESET',
      action: 'click_convert_to_ruleset',
      label: ['ref_cta:convert_bp_to_ruleset', 'ref_loc:rulesets_overview_convert_to_ruleset_button'].join(';'),
    },
    redundant: {
      category: 'MANAGE_BRANCH_PROTECTION',
      action: 'click_manage_branch_protections',
      label: ['ref_cta:redundant_bp', 'ref_loc:rulesets_overview_manage_branch_protections_button'].join(';'),
    },
  }

  const anyProtectedBranchesNotFullyCovered = rulesetConversionInfo?.anyProtectedBranchesNotFullyCovered ?? false
  const anyRedundantProtectedBranches = rulesetConversionInfo?.anyRedundantProtectedBranches ?? false
  const conversionPaths = rulesetConversionInfo?.conversionPaths

  const sharedConversionConditions =
    convertToRulesetUIBanner && sourceType === 'repository' && !readOnly && !isStafftools && !conversionBannerDismissed
  const presentConversionBannerToUser =
    sharedConversionConditions && anyProtectedBranchesNotFullyCovered && conversionPaths
  const presentRedundantBranchProtectionsBannerToUser =
    sharedConversionConditions && !presentConversionBannerToUser && anyRedundantProtectedBranches

  return (
    /* eslint-disable-next-line @github-ui/github-monorepo/no-global-classname-literals */
    <div className="settings-next position-relative" {...hpcAttrs()}>
      {presentConversionBannerToUser && (
        <Banner
          // Navigator focuses the page <h1> after soft navigation, skipping this preceding banner.
          // Mark it as the autofocus target so screen-reader users don't miss the conversion CTA.
          data-react-autofocus
          variant="info"
          title="You have branch protection rules that can be converted"
          description="Rulesets offer layered enforcement, evaluate mode, and richer branch targeting. Import your existing rules to get started."
          primaryAction={
            <ImportBranchProtectionDialog
              paths={conversionPaths}
              onOpen={() => sendClickAnalyticsEvent(analytics.convert)}
              onSuccess={() => navigate(resolvePath(''))}
            >
              <ImportBranchProtectionDialog.AnchorButton>
                Import branch protection rules
              </ImportBranchProtectionDialog.AnchorButton>
            </ImportBranchProtectionDialog>
          }
          onDismiss={() => {
            // Move focus to the page heading before the banner unmounts so keyboard and
            // screen-reader users are not stranded when the focused dismiss button disappears.
            headingRef.current?.focus()
            setDismissedSourceId(source.id)
          }}
          className={styles.BPConversionBanner}
        />
      )}
      {presentRedundantBranchProtectionsBannerToUser && (
        <RedundantProtectedBranchesBanner
          onClick={() => {
            const repo = source as Repository
            sendClickAnalyticsEvent(analytics.redundant)
            // eslint-disable-next-line @github-ui/web-vitals/no-sync-navigation-in-input-handler
            navigate(branchProtectionsPath({owner: repo.ownerLogin, repo: repo.name}))
          }}
          onDismiss={() => {
            headingRef.current?.focus()
            setDismissedSourceId(source.id)
          }}
        />
      )}
      <Subhead
        heading={heading}
        alphaOrBeta={alphaOrBeta}
        headingRef={headingRef}
        renderAction={() => {
          return readOnly ? null : rulesets.length > 0 ? (
            <NewRulesetButton
              rulesetsUrl={resolvePath('./')}
              setFlashAlert={setFlashAlert}
              className={styles.NewRulesetButton}
            />
          ) : null
        }}
      />
      <DismissibleFlashOrToast flashAlert={flashAlert} setFlashAlert={setFlashAlert} ref={flashRef} />
      <div className="d-flex flex-column gap-3">
        {flashMessages.map(flashMessage =>
          flashMessage.message ? (
            <Flash key={flashMessage.message} variant="default">
              {renderFlashMessage(flashMessage.message)}
            </Flash>
          ) : null,
        )}
        {upsellInfo.rulesets.cta.visible && inheritedSourceTypes.length > 0 && (
          <FlashUpsellBanner ctaType="rulesets" sourceType={sourceType} />
        )}
        {sourceType === 'repository' && supportsGitRefTargets && rulesets.length > 0 ? (
          <div>
            <BranchSelector gitRef={branch} repo={source as Repository} branchListCacheKey={branchListCacheKey} />
          </div>
        ) : null}
        {inheritedSourceTypes.length > 0 ? (
          inheritedSourceTypes.map(key => (
            <Fragment key={key}>
              <BorderBox
                name={`${capitalize(key)} ${rulesetsOrPolicies}`}
                showHeader={inheritedSourceTypes.length > 1 || !inheritedSourceTypes.includes(capitalize(sourceType))}
                renderCreateButton={() => {
                  // Don't show managed by if the source type is the same as the group's type
                  if (sourceType === key.toLowerCase()) {
                    return null
                  }

                  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                  const ownedByName = rulesetsBySource[key]![0]!.source.name
                  return <div className="text-small color-fg-muted mt-1">Managed by {ownedByName}</div>
                }}
              >
                <RulesetList
                  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                  rulesets={rulesetsBySource[key]!}
                  sourceType={sourceType}
                  source={source}
                  readOnly={readOnly}
                  setFlashAlert={setFlashAlert}
                />
              </BorderBox>

              {!readOnly &&
                key.toLowerCase() !== sourceType &&
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                editableRulesets.includes(rulesetsBySource[key]![0]!.id) && (
                  <div className="text-small">
                    <span className="text-bold">Tip:</span>{' '}
                    <span className="color-fg-muted">
                      {/* eslint-disable-next-line @typescript-eslint/no-non-null-assertion */}
                      As a {rulesetsBySource[key]![0]?.source.name} admin, you can
                      {/* eslint-disable-next-line @typescript-eslint/no-non-null-assertion */}
                      <a href={rulesetsBySource[key]![0]?.source.url}>
                        {' '}
                        manage {source.name} {rulesetsOrPolicies} in {key.toLocaleLowerCase()} settings.
                      </a>
                    </span>
                  </div>
                )}
            </Fragment>
          ))
        ) : (
          <>
            {hasFilteredRulesets ? (
              <Blankslate heading={`No ${rulesetsOrPolicies} matched your search`}>
                {`Try expanding your search or creating a new ${rulesetOrPolicy}`}
              </Blankslate>
            ) : (
              <UpsellBanner
                headingText={headingText}
                withBorder={false}
                showNewRulesetButton={!readOnly}
                infoVariant="two"
                sourceType={sourceType}
                ctaType="rulesets"
                setFlashAlert={setFlashAlert}
              />
            )}
          </>
        )}
        {isRuleConfigListEnabled && branch && paths?.configList ? (
          <RuleConfigList refName={branch} path={paths.configList} />
        ) : null}
      </div>
    </div>
  )
}
